/**
 * Categories API wrapper
 * Provides simplified interface for category-related operations
 */

import type {
	CategoryList,
	CategoryRead,
	CategoryCreate,
	CategoryUpdate,
	CategoryTree
} from './generated';
import {
	getCategoriesApiCategoriesGet,
	createCategoryApiCategoriesPost,
	updateCategoryApiCategoriesCategoryIdPut,
	deleteCategoryApiCategoriesCategoryIdDelete,
	updateCategoryParentApiCategoriesCategoryIdParentPut,
	reorderCategoriesApiCategoriesReorderPut,
	getCategoryTreeOrderedApiCategoriesTreeOrderedGet
} from './generated/categories/categories';
import { createApiRequestError, isApiFailureCode } from '$lib/utils/apiErrors';

export class CategoriesAPI {
	/**
	 * Get all categories for the current user
	 */
	static async getCategories(): Promise<CategoryList> {
		const response = await getCategoriesApiCategoriesGet();
		return response;
	}

	/**
	 * Create a new category
	 */
	static async createCategory(data: CategoryCreate): Promise<CategoryRead> {
		try {
			const response = await createCategoryApiCategoriesPost(data);
			return response;
		} catch (error) {
			if (isApiFailureCode(error, 'category_limit_reached')) {
				throw error;
			}
			throw createApiRequestError('CATEGORY_REQUEST_FAILED');
		}
	}

	/**
	 * Update a category
	 */
	static async updateCategory(categoryId: string, data: CategoryUpdate): Promise<CategoryRead> {
		try {
			const response = await updateCategoryApiCategoriesCategoryIdPut(categoryId, data);
			return response;
		} catch {
			throw createApiRequestError('CATEGORY_REQUEST_FAILED');
		}
	}

	/**
	 * Delete a category
	 */
	static async deleteCategory(categoryId: string): Promise<void> {
		try {
			await deleteCategoryApiCategoriesCategoryIdDelete(categoryId);
			// API returns void/empty response for 204
		} catch {
			throw createApiRequestError('CATEGORY_REQUEST_FAILED');
		}
	}

	/**
	 * Update a category's parent (move in hierarchy or make top-level)
	 */
	static async updateCategoryParent(
		categoryId: string,
		parentId: string | null
	): Promise<CategoryRead> {
		try {
			return await updateCategoryParentApiCategoriesCategoryIdParentPut(categoryId, {
				parent_id: parentId
			});
		} catch {
			throw createApiRequestError('CATEGORY_REQUEST_FAILED');
		}
	}

	/**
	 * Reorder categories according to provided list of category IDs
	 */
	static async reorderCategories(orderedCategoryIds: string[]): Promise<CategoryRead[]> {
		try {
			return await reorderCategoriesApiCategoriesReorderPut({
				ordered_category_ids: orderedCategoryIds
			});
		} catch {
			throw createApiRequestError('CATEGORY_REQUEST_FAILED');
		}
	}

	/**
	 * Get ordered category tree
	 */
	static async getCategoryTreeOrdered(): Promise<CategoryTree[]> {
		try {
			return await getCategoryTreeOrderedApiCategoriesTreeOrderedGet();
		} catch {
			throw createApiRequestError('CATEGORY_REQUEST_FAILED');
		}
	}
}

// Re-export types for convenience
export type {
	CategoryRead,
	CategoryList,
	CategoryCreate,
	CategoryUpdate,
	CategoryTree
} from './generated';
